import { jsPDF } from "jspdf";

const DARK = [11, 15, 23];
const SLATE = [100, 116, 139];
const INK = [15, 23, 42];
const when = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

function coverPage(doc, d, W, H, brandMark) {
  doc.setFillColor(...DARK); doc.rect(0, 0, W, H, "F");
  if (d.branding?.cover) { try { doc.addImage(d.branding.cover, "JPEG", 0, 0, W, 300); } catch (e) { /* skip */ } }
  let y = 372;
  if (d.branding?.logo) { try { doc.addImage(d.branding.logo, "PNG", 48, y - 46, 84, 84); } catch (e) { /* skip */ } }
  const tx = d.branding?.logo ? 148 : 48;
  doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(26);
  doc.text(doc.splitTextToSize(d.project?.title || "Project photo report", W - tx - 48), tx, y);
  y += 54;
  doc.setTextColor(148, 163, 184); doc.setFont("helvetica", "normal"); doc.setFontSize(13);
  if (d.project?.company) { doc.text(d.project.company, 48, y); y += 20; }
  if (d.project?.address) { doc.text(d.project.address, 48, y); y += 20; }
  doc.setFontSize(10); doc.setTextColor(...SLATE);
  doc.text(`${d.count} photos - generated ${when(d.generated_at)}`, 48, y);
  if (brandMark) {
    try { doc.addImage(brandMark, "PNG", W - 176, H - 46, 16, 16); } catch (e) { /* skip */ }
    doc.setTextColor(...SLATE); doc.setFontSize(8);
    doc.text("Powered by SiteControlHQ", W - 154, H - 35);
  }
}

function signoffPage(doc, d, W, H) {
  doc.addPage(); doc.setFillColor(255, 255, 255); doc.rect(0, 0, W, H, "F");
  const M = 48; let y = 72;
  doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(18);
  doc.text("Client approval & sign-off", M, y); y += 30;
  doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(...SLATE);
  const intro = doc.splitTextToSize(
    "I confirm I have reviewed the photo documentation in this report and approve the work as shown.", W - M * 2);
  doc.text(intro, M, y); y += intro.length * 15 + 40;
  const line = (label) => {
    doc.setDrawColor(150, 163, 184); doc.line(M, y, W - M, y);
    doc.setTextColor(...SLATE); doc.setFontSize(9); doc.text(label, M, y + 14); y += 60;
  };
  doc.setFontSize(10); doc.setTextColor(...INK);
  line("Client name (print)");
  line("Signature");
  line("Date");
}

export function buildPhotoReport(d, opts = {}) {
  const perPage = [1, 2, 4].includes(opts.perPage) ? opts.perPage : 4;
  const signoff = !!opts.signoff;
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  coverPage(doc, d, W, H, opts.brandMark);

  const M = 40, gap = 18, top = 52;
  const cols = perPage === 4 ? 2 : 1;
  const cw = (W - M * 2 - (cols - 1) * gap) / cols;
  const ih = perPage === 1 ? cw * 0.6 : (perPage === 2 ? cw * 0.42 : cw * 0.66);
  const capH = perPage === 1 ? 72 : 56;
  const cellH = ih + capH;

  (d.photos || []).forEach((p, i) => {
    const pos = i % perPage;
    if (pos === 0) {
      doc.addPage(); doc.setFillColor(255, 255, 255); doc.rect(0, 0, W, H, "F");
      doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(12);
      doc.text(d.project?.title || "Photos", M, 32);
      doc.setDrawColor(226, 232, 240); doc.line(M, 40, W - M, 40);
    }
    const r = Math.floor(pos / cols);
    const col = pos % cols;
    const x = M + col * (cw + gap);
    const cellY = top + r * (cellH + gap);
    try { doc.addImage(p.data, "JPEG", x, cellY, cw, ih); }
    catch (e) { doc.setDrawColor(200); doc.rect(x, cellY, cw, ih); }
    let cy = cellY + ih + 15;
    doc.setTextColor(...INK); doc.setFont("helvetica", "normal"); doc.setFontSize(perPage === 1 ? 11 : 9);
    const cap = doc.splitTextToSize(p.caption || "(no caption)", cw).slice(0, perPage === 1 ? 3 : 2);
    doc.text(cap, x, cy); cy += cap.length * (perPage === 1 ? 13 : 11) + 3;
    doc.setTextColor(...SLATE); doc.setFontSize(7.5);
    doc.text(`${when(p.at)}${p.taker ? " - " + p.taker : ""}`, x, cy);
  });

  if (signoff) signoffPage(doc, d, W, H);

  const pages = doc.internal.getNumberOfPages();
  for (let i = 2; i <= pages; i++) {
    doc.setPage(i); doc.setFontSize(8); doc.setTextColor(...SLATE);
    doc.text(`${d.project?.company || "SiteControlHQ"} - ${d.project?.title || ""} - page ${i - 1}/${pages - 1}`,
      W / 2, H - 22, { align: "center" });
    if (opts.brandMark) {
      try { doc.addImage(opts.brandMark, "PNG", 40, H - 30, 11, 11); } catch (e) { /* skip */ }
      doc.text("SiteControlHQ", 55, H - 22);
    }
  }
  return doc;
}

async function loadDataUrl(src) {
  const res = await fetch(src);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(blob);
  });
}

export async function generatePhotoReport(d, opts = {}) {
  let brandMark = null;
  try { brandMark = await loadDataUrl("/schq-mark-transparent.png"); } catch (e) { /* skip */ }
  const name = (d.project?.title || "photo-report").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  buildPhotoReport(d, { ...opts, brandMark }).save(`${name}-photo-report.pdf`);
}
